import PropTypes from "prop-types"
import { useEffect, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"
import * as yup from "yup"
import Input from "~/components/ReactHookForms/Input"
import Select from "~/components/ReactHookForms/Select"
import TextArea from "~/components/ReactHookForms/TextArea"
import { SRT_LANGUAGES } from "~/constants/languages"
import SharedDialog from "~/dialogs/SharedDialog"
import useSetTranslationMutation from "~/hooks/useSetTranslationMutation"
import useYupValidationResolver from "~/hooks/useYupValidationResolver"

const AddTranslationDialog = ({
  open,
  closeDialog,
  translation,
  typeData,
  translations,
}) => {
  const { type } = typeData

  const createTranslationSchema = yup.object().shape({
    locale: yup.string().required(),
    ...(type === "docs"
      ? {
          doc_name: yup.string().required(),
          doc_type: yup.string().required(),
        }
      : {
          name: yup.string().required(),
          description: yup.string().required(),
        }),
  })

  const resolver = useYupValidationResolver(createTranslationSchema)

  const { mutateAsync } = useSetTranslationMutation(typeData)

  const methods = useForm({
    resolver,
    defaultValues: {
      id: null,
      locale: "",
      ...(type === "docs"
        ? {
            doc_name: "",
            doc_type: "",
          }
        : {
            name: "",
            description: "",
          }),
    },
  })

  useEffect(() => {
    if (translation) {
      methods.reset({
        id: translation.id ?? null,
        locale: translation.locale ?? "",
        ...(type === "docs"
          ? {
              doc_name: translation.doc_name ?? "",
              doc_type: translation.doc_type ?? "",
            }
          : {
              name: translation.name ?? "",
              description: translation.description ?? "",
            }),
      })
    }
  }, [translation])

  const onSubmitHandler = async (data) => {
    await mutateAsync(data)
      .then((res) => {
        closeDialog()
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const langOptions = useMemo(() => {
    return Object.keys(SRT_LANGUAGES)
      .map((language) => {
        // filter translations that already exist
        const localeArray = translations.map((x) => x.locale)
        if (!translation && localeArray.includes(language)) return null
        return {
          id: language,
          name: `${SRT_LANGUAGES[language]} - ${language}`,
        }
      })
      .filter((x) => x)
  }, [translations, translation])

  return (
    <SharedDialog
      isOpen={open}
      onClose={() => {
        closeDialog()
        methods?.reset()
      }}
      action={methods?.handleSubmit(onSubmitHandler)}
      actionTitle={translation.id ? "Update" : "Add"}
    >
      <div className="aspect-video">
        <h2>{type === "docs" ? "Doc" : "Course"} Translation</h2>
        <p>{translation.id ? "Update" : "Create"} translation</p>
        <FormProvider {...methods}>
          <Select
            disabled={translation.locale}
            label="Lang"
            id="locale"
            options={langOptions}
            className="h-[75px]"
          />
          <Input
            label={type === "docs" ? "Doc Name" : "Course Name"}
            id={type === "docs" ? "doc_name" : "name"}
          />

          {type === "docs" ? (
            <Input label="Doc Type" id="doc_type" />
          ) : (
            <TextArea label="Course Description" id="description" />
          )}
        </FormProvider>
      </div>
    </SharedDialog>
  )
}

AddTranslationDialog.propTypes = {
  open: PropTypes.bool,
  closeDialog: PropTypes.func,
  translation: PropTypes.object,
  typeData: PropTypes.object,
  translations: PropTypes.array,
}

export default AddTranslationDialog
